// getBBox for scene nodes.
//
// Three callers depend on this: the app tile scales its label to fit, a scope
// callout sizes its tail from its text, and the overlay measures each callout
// before placing it. All three want what SVG's getBBox gives — the geometry box
// in the element's own user space, with the element's own transform excluded and
// stroke width not counted.
//
// Unlike the DOM version this is pure arithmetic over the retained tree, so it
// does not flush layout and can be called at any point in a frame.

import {
  identity,
  multiply,
  parseTransform,
  applyX,
  applyY,
} from "../scene/matrix";
import { resolveStyle, isHidden, ROOT_STYLE } from "../scene/style";
import { flattenPath } from "./path";
import { fontKeyFor, fontMetrics, baselineOffset, layoutText } from "./text";
import { num } from "./geometry";

export function computeBBox(node) {
  const box = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };

  // Style is resolved from this node's inherited context so that a font size set
  // on an ancestor is honoured, matching what the renderer will draw.
  const inherited = inheritedStyleFor(node);

  walk(node, identity(), inherited, box, true);

  if (box.minX > box.maxX) return { x: 0, y: 0, width: 0, height: 0 };

  return {
    x: box.minX,
    y: box.minY,
    width: box.maxX - box.minX,
    height: box.maxY - box.minY,
  };
}

// Walks down from the root so inherited presentation attributes resolve the same
// way the batch builder resolves them.
function inheritedStyleFor(node) {
  const chain = [];
  let current = node.parentNode;
  while (current) {
    chain.push(current);
    current = current.parentNode;
  }

  let style = ROOT_STYLE;
  for (let i = chain.length - 1; i >= 0; i--)
    style = resolveStyle(chain[i], style);
  return style;
}

function walk(node, matrix, parentStyle, box, isRoot) {
  if (node.nonRendering) return;
  if (!isRoot && isHidden(node)) return;

  // The element's own transform is outside its bounding box by definition.
  let local = matrix;
  if (!isRoot) {
    const own = parseTransform(node.attrs.transform);
    if (own) local = multiply(matrix, own);
  }

  const style = resolveStyle(node, parentStyle);

  addOwnGeometry(node, local, style, box);

  const children = node.children;
  for (let i = 0; i < children.length; i++)
    walk(children[i], local, style, box, false);
}

function addOwnGeometry(node, matrix, style, box) {
  const attrs = node.attrs;

  switch (node.tagName) {
    case "circle": {
      const cx = num(attrs.cx, 0);
      const cy = num(attrs.cy, 0);
      const r = num(attrs.r, 0);
      if (r > 0) addRect(box, matrix, cx - r, cy - r, cx + r, cy + r);
      break;
    }
    case "rect":
    case "image": {
      const x = num(attrs.x, 0);
      const y = num(attrs.y, 0);
      const w = num(attrs.width, 0);
      const h = num(attrs.height, 0);
      if (w > 0 && h > 0) addRect(box, matrix, x, y, x + w, y + h);
      break;
    }
    case "line":
      addRect(
        box,
        matrix,
        Math.min(num(attrs.x1, 0), num(attrs.x2, 0)),
        Math.min(num(attrs.y1, 0), num(attrs.y2, 0)),
        Math.max(num(attrs.x1, 0), num(attrs.x2, 0)),
        Math.max(num(attrs.y1, 0), num(attrs.y2, 0))
      );
      break;
    case "path": {
      const subpaths = flattenPath(attrs.d);
      for (let i = 0; i < subpaths.length; i++) {
        const points = subpaths[i].points;
        for (let p = 0; p < points.length; p += 2)
          addPoint(box, matrix, points[p], points[p + 1]);
      }
      break;
    }
    case "text":
      addTextBox(node, matrix, style, box);
      break;
    default:
      break;
  }
}

function addTextBox(node, matrix, style, box) {
  const content = node.textContent;
  if (!content) return;

  const size = style.fontSize;
  if (!(size > 0)) return;

  const key = fontKeyFor(style);
  const metrics = fontMetrics(key);
  const layout = layoutText(content, key);
  const spacing = style.letterSpacing || 0;

  const advance = layout.width * size + spacing * layout.clusters.length;

  let x = num(node.attrs.x, 0);
  if (style.textAnchor === "middle") x -= advance / 2;
  else if (style.textAnchor === "end") x -= advance;

  const baseline =
    num(node.attrs.y, 0) +
    baselineOffset(style.dominantBaseline, metrics) * size;

  addRect(
    box,
    matrix,
    x,
    baseline - metrics.ascent * size,
    x + advance,
    baseline + metrics.descent * size
  );
}

function addRect(box, matrix, x0, y0, x1, y1) {
  addPoint(box, matrix, x0, y0);
  addPoint(box, matrix, x1, y0);
  addPoint(box, matrix, x1, y1);
  addPoint(box, matrix, x0, y1);
}

function addPoint(box, matrix, x, y) {
  const px = applyX(matrix, x, y);
  const py = applyY(matrix, x, y);
  if (px < box.minX) box.minX = px;
  if (px > box.maxX) box.maxX = px;
  if (py < box.minY) box.minY = py;
  if (py > box.maxY) box.maxY = py;
}
